import React from 'react';
import { gql, TypedDocumentNode } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { parameterize } from '@moda/portal-stanchions';
import {
  Gender,
  TrunkshowIndexPageCollectionsQuery,
  TrunkshowIndexPageCollectionsQueryVariables
} from '../../generated/types';
import { throwQueryError } from '../../lib/apollo/throwQueryError';
import { usePLPParams } from '../../hooks/usePLPParams';
import {
  TRUNKSHOW_LISTING_PAGE_FRAGMENT,
  TrunkshowListing
} from '../../components/TrunkshowListing';
import { Loading } from '../../components/Loading';

const TRUNKSHOW_INDEX_PAGE_COLLECTIONS_QUERY: TypedDocumentNode<
  TrunkshowIndexPageCollectionsQuery,
  TrunkshowIndexPageCollectionsQueryVariables
> = gql`
  query TrunkshowIndexPageCollectionsQuery(
    $slug: String!
    $vertical: Gender
    $categories: [String]
    $designers: [String]
    $sort: String
    $versionId: Int
    $ruleContexts: [String]
  ) {
    trunkshows(
      slug: $slug
      input: {
        gender: $vertical
        category: $categories
        designer_slugs: $designers
        version_id: $versionId
        rule_contexts: $ruleContexts
      }
    ) {
      ...TrunkshowListingPageFragment
    }
  }
  ${TRUNKSHOW_LISTING_PAGE_FRAGMENT}
`;

type Props = {
  title: string;
  description: string;
  loading: boolean;
};

export const TrunkshowIndexPageCollections: React.FC<Props> = ({
  title,
  description,
  loading: loadingPage
}) => {
  const {
    params: { category, designers, sort, versionId }
  } = usePLPParams();

  const {
    data: currentData,
    previousData,
    loading,
    error
  } = useQuery(TRUNKSHOW_INDEX_PAGE_COLLECTIONS_QUERY, {
    variables: {
      // TODO: remove women from this (requires changes in mojo/search-api)
      slug: 'women/trunkshows',
      ruleContexts: ['trunkshow_index'],
      vertical: Gender.women,
      categories: category ? [category.replace(/-/g, ' ')] : [],
      designers,
      sort: parameterize(sort, '_'),
      versionId
    }
  });

  const data = currentData ?? previousData;

  if (error) throwQueryError(error);

  if (!data?.trunkshows) return loadingPage ? null : <Loading />;

  return (
    <TrunkshowListing
      title={title}
      description={description}
      queryResult={data?.trunkshows}
      loading={loading || loadingPage}
    />
  );
};
